/* eslint-disable no-unused-vars */
import { useState, useEffect, useCallback } from "react";
import { AuthService } from "../../../services/auth.service";
import api from "../../../services/api.service";
import GeneralModal from "../../modals/GeneralModal";
import ConfirmRemoveModal from "../../modals/ConfirmRemoveModal";

const FileManagement = () => {
  // States สำหรับข้อมูลไฟล์และการแสดงผล
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("");
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleItems, setVisibleItems] = useState(8);

  // States สำหรับ search & suggestions
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // States สำหรับ modal แจ้งข้อความ และ Confirm Remove Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");

  const userRole = AuthService.getUserRole();

  // Fetch files ใน directory ปัจจุบัน
  const fetchFiles = useCallback(async (folderPath = "") => {
    setLoading(true);
    try {
      const response = await api.get("/sftp/folder/files", {
        params: { folderPath },
      });
      setFiles(response.data.items);
      setFilteredItems(response.data.items);
      setCurrentPath(folderPath);
      localStorage.setItem("lastPath", folderPath);
      updateBreadcrumbs(folderPath);
      setVisibleItems(8); // Reset visible items when loading new folder
      setError(null);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch files");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const lastPath = localStorage.getItem("lastPath") || "";
    fetchFiles(lastPath);
  }, [fetchFiles]);

  // Update breadcrumbs จาก currentPath
  const updateBreadcrumbs = (path) => {
    const parts = path ? path.split("/") : [];
    const crumbs = [
      { name: "Home", path: "" },
      ...parts.map((part, index) => ({
        name: part,
        path: parts.slice(0, index + 1).join("/"),
      })),
    ];
    setBreadcrumbs(crumbs);
  };

  // เมื่อคลิกที่ folder ให้สร้าง path ใหม่และ fetch files
  const handleFolderClick = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    fetchFiles(newPath);
  };

  // ฟังก์ชันสำหรับ breadcrumb เมื่อคลิก
  const handleBreadcrumbClick = (path) => {
    fetchFiles(path);
  };

  // Handle file download
  const handleDownload = async (fileName) => {
    try {
      const response = await api.get("/sftp/files/download", {
        params: { filePath: `${currentPath}/${fileName}` },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError("Failed to download file");
    }
  };

  // เมื่อคลิกปุ่มลบไฟล์ (แทน window.confirm)
  const handleDeleteClick = (fileName) => {
    setSelectedFile(fileName);
    setShowConfirmModal(true);
  };

  // เมื่อผู้ใช้ยืนยันใน Confirm Modal ให้ดำเนินการลบไฟล์
  const handleDeleteConfirmed = async () => {
    try {
      await api.delete("/sftp/files/delete", {
        data: { filePath: `${currentPath}/${selectedFile}` },
      });
      fetchFiles(currentPath); // รีเฟรชรายการไฟล์
      setModalMessage("File deleted successfully");
      setIsSuccess(true);
    } catch (error) {
      setModalMessage("Failed to delete file");
      setIsSuccess(false);
    } finally {
      setShowConfirmModal(false);
      setModalVisible(true);
      setSelectedFile("");
    }
  };

  // เมื่อผู้ใช้ยกเลิกการลบใน Confirm Modal
  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedFile("");
  };

  // Search & Suggestion functions
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredItems(files);
      setSuggestions([]);
    } else {
      const filtered = files.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredItems(filtered);

      const uniqueSuggestions = [
        ...new Set(
          files
            .filter((item) =>
              item.name.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((item) => item.name)
        ),
      ];
      setSuggestions(uniqueSuggestions.slice(0, 5));
    }
  }, [searchQuery, files]);

  const handleSearch = () => {
    // การค้นหาถูกควบคุมผ่าน useEffect
    setShowSuggestions(false);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setFilteredItems(files);
    setVisibleItems(8); // Reset visible items when clearing search
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
  };

  // Render file/folder item ใน table
  const renderItem = (item) => (
    <tr key={item.fullPath || item.name}>
      <td>
        {item.isDirectory ? (
          <a
            onClick={() => handleFolderClick(item.name)}
            className="cursor-pointer text-black hover:text-gray-400 flex items-center"
          >
            <i
              className="fa-solid fa-folder mr-2"
              style={{ color: "#f4b451" }}
            ></i>
            {item.name}
          </a>
        ) : (
          <div className="flex items-center">
            <i className="fa-solid fa-file mr-2 text-gray-400"></i>
            {item.name}
          </div>
        )}
      </td>
      <td>{item.size}</td>
      <td>{new Date(item.modifyTime).toLocaleString()}</td>
      <td>
        {!item.isDirectory && (
          <>
            <div className="tooltip" data-tip="Download">
              <button
                className="btn btn-sm text-blue-600"
                onClick={() => handleDownload(item.name)}
              >
                <i className="fa-solid fa-download"></i>
              </button>
            </div>
            {(userRole === "admin" || userRole === "systemAdmin") && (
              <div className="tooltip" data-tip="Remove File">
                <button
                  className="btn btn-sm text-red-600 ml-2"
                  onClick={() => handleDeleteClick(item.name)}
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            )}
          </>
        )}
      </td>
    </tr>
  );

  return (
<div className="container mx-auto max-w-screen-xl px-3 sm:px-6 md:px-8 py-6 sm:py-8 my-4 sm:my-6">
        <div className="bg-white shadow-md rounded-xl p-4 sm:p-6">
          {/* Header and Search Section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div className="flex items-baseline">
              <i className="fas fa-file fa-lg sm:fa-xl mr-2 sm:mr-4"></i>
              <h1 className="text-lg sm:text-xl font-bold">File Explorer</h1>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full sm:w-auto gap-2 sm:gap-4">
              <div className="relative flex-1 sm:flex-none">
                <input
                  type="text"
                  placeholder="Search by file name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => {
                    if (searchQuery.trim() !== "") setShowSuggestions(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                    if (e.key === "Escape") handleClearSearch();
                  }}
                  className="input input-bordered w-full sm:w-64 md:w-96 pl-10 text-sm sm:text-base"
                  onClick={() => {
                    if (searchQuery.trim() !== "") setShowSuggestions(true);
                  }}
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  <i className="fas fa-search"></i>
                </span>
                {showSuggestions && suggestions.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 max-h-48 sm:max-h-60 overflow-y-auto shadow-lg">
                    {suggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {searchQuery && (
                <button
                  className="btn btn-outline btn-error btn-sm sm:btn-md w-full sm:w-auto"
                  onClick={handleClearSearch}
                >
                  <i className="fa-solid fa-filter-circle-xmark mr-2"></i>
                  <span className="hidden sm:inline">Clear Search</span>
                  <span className="sm:hidden">Clear</span>
                </button>
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="flex flex-col mt-4 h-[calc(100vh-16rem)] overflow-hidden">
            {/* Breadcrumbs */}
            <div className="breadcrumbs text-xs sm:text-sm overflow-x-auto whitespace-nowrap mb-2 sm:mb-4">
              <ul className="flex space-x-2">
                {breadcrumbs.map((crumb) => (
                  <li key={crumb.path} className="inline-flex items-center">
                    <a
                      onClick={() => handleBreadcrumbClick(crumb.path)}
                      className="inline-flex items-center font-normal text-gray-700 hover:text-blue-600 cursor-pointer ml-1 sm:ml-2"
                    >
                      {crumb.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Loading Indicator */}
            {loading && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                <div className="loading loading-dots loading-lg text-white"></div>
              </div>
            )}

            {/* Files Table */}
            <div className="overflow-auto h-full">
              <table className="table table-pin-rows table-pin-cols w-full text-sm sm:text-base">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-xs sm:text-sm">Folder / File Name</th>
                    <th className="text-xs sm:text-sm hidden sm:table-cell">Size</th>
                    <th className="text-xs sm:text-sm hidden md:table-cell">Last Modified</th>
                    <th className="text-xs sm:text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems && filteredItems.length > 0 ? (
                    filteredItems.slice(0, visibleItems).map((item) =>
                      renderItem(item)
                    )
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-sm">
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* See More Link */}
              {filteredItems.length > visibleItems && (
                <div className="flex justify-center mt-3 sm:mt-4 mb-2">
                  <span
                    className="text-blue-600 hover:text-blue-800 cursor-pointer text-sm sm:text-base"
                    onClick={() => setVisibleItems((prev) => prev + 8)}
                  >
                    See more
                  </span>
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && <div className="text-red-500 text-sm sm:text-base mb-4">{error}</div>}
          </div>
        {/* General Modal */}
        <GeneralModal
          isVisible={modalVisible}
          message={modalMessage}
          onClose={() => setModalVisible(false)}
          isSuccess={isSuccess}
        />

        {/* Confirm Remove Modal */}
        {showConfirmModal && (
          <ConfirmRemoveModal
            handleDeleteUser={handleDeleteConfirmed}
            handleCloseModal={handleCloseConfirmModal}
          />
        )}
      </div>
    </div>
  );
};

export default FileManagement;